import PropTypes from "prop-types"
import React from "react"

import Button from "src/components/Button"
import OverflowMenu from "src/components/OverflowMenu"

import ColumnSettings from "./ColumnSettings"
import { ActionPropType, ColumnPropType } from "./types"

const MAX_VISIBLE_BULK_ACTIONS = 5

const TableToolbar = ({
  actions = [],
  selection = [],
  selectedCount = 0,
  totalCount,
  pageSelectableCount,
  onSelectAll,
  onDeselectAll,
  allSelectableRowsSelected,
  showSelectAllBanner,
  onSelectAllAcrossPages,
  infinite = false,
  selectAllCount,
  hasBulkActions = false,
  onRequestConfirm,
  hideSettings = false,
  columns = [],
  onToggleColumn,
}) => {
  const bulkActions = actions.filter((action) => action.onClickBulk)
  const visibleBulkActions = bulkActions.slice(0, MAX_VISIBLE_BULK_ACTIONS)
  const overflowBulkActions = bulkActions.slice(MAX_VISIBLE_BULK_ACTIONS)
  const showBulkControls = bulkActions.length > 0
  const showToolbar = showBulkControls || !hideSettings

  if (!showToolbar) {
    return null
  }

  const hasSelection = selectedCount > 0
  const noSelectableRowsOnPage = pageSelectableCount === 0
  const noSelectableRowsAtAll = totalCount === 0

  const handleActionClick = (action) => {
    if (action.confirm && onRequestConfirm) {
      onRequestConfirm(action, null, selection)
    } else if (action.onClickBulk) {
      action.onClickBulk(selection)
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex h-10 items-center justify-between rounded-t bg-white">
        {/* Left side - bulk actions, sticky left */}
        <div className="sticky left-0 z-10 mr-4 flex items-center gap-4 bg-white px-4">
          {hasBulkActions && (
            <>
              <Button
                variant="link"
                onClick={onSelectAll}
                disabled={
                  allSelectableRowsSelected ||
                  noSelectableRowsOnPage ||
                  noSelectableRowsAtAll
                }
              >
                {selectAllCount
                  ? `Select all ${selectAllCount.toLocaleString()}`
                  : infinite
                  ? "Select all"
                  : "Select"}
              </Button>
              <Button
                variant="link"
                onClick={onDeselectAll}
                disabled={!hasSelection}
              >
                Deselect all
              </Button>
              {hasSelection && (
                <>
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold">
                      {selectedCount.toLocaleString()}
                    </span>{" "}
                    selected
                  </span>
                  {bulkActions.length > 0 && (
                    <span className="text-gray-300">|</span>
                  )}
                  {visibleBulkActions.map((action, index) => (
                    <Button
                      key={index}
                      variant="secondary"
                      small={true}
                      icon={action.icon ? `fa-${action.icon}` : undefined}
                      iconStyle={action.iconStyle}
                      onClick={() => handleActionClick(action)}
                      disabled={action.disabled}
                    >
                      {action.label}
                    </Button>
                  ))}
                  {overflowBulkActions.length > 0 && (
                    <OverflowMenu alignEnd>
                      {overflowBulkActions.map((action, index) => (
                        <OverflowMenu.Item
                          key={index}
                          label={action.label}
                          onClick={() => handleActionClick(action)}
                        />
                      ))}
                    </OverflowMenu>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Right side - settings cog, sticky right */}
        {!hideSettings && (
          <div className="sticky right-0 z-10 bg-white pr-2">
            <ColumnSettings columns={columns} onToggleColumn={onToggleColumn} />
          </div>
        )}
      </div>

      {showSelectAllBanner && (
        <div className="flex items-center justify-center gap-1 border-b bg-blue-50 py-2 text-sm">
          <span className="text-gray-600">
            {infinite
              ? `All ${pageSelectableCount} loaded selected.`
              : `All ${pageSelectableCount} on this page selected.`}
          </span>
          <Button variant="link" onClick={onSelectAllAcrossPages}>
            Select all {totalCount?.toLocaleString()} items
          </Button>
        </div>
      )}
    </div>
  )
}

TableToolbar.propTypes = {
  actions: PropTypes.arrayOf(ActionPropType),
  selection: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  selectedCount: PropTypes.number,
  totalCount: PropTypes.number,
  pageSelectableCount: PropTypes.number,
  onSelectAll: PropTypes.func,
  onDeselectAll: PropTypes.func,
  allSelectableRowsSelected: PropTypes.bool,
  showSelectAllBanner: PropTypes.bool,
  onSelectAllAcrossPages: PropTypes.func,
  infinite: PropTypes.bool,
  selectAllCount: PropTypes.number,
  hasBulkActions: PropTypes.bool,
  onRequestConfirm: PropTypes.func,
  hideSettings: PropTypes.bool,
  columns: PropTypes.arrayOf(ColumnPropType),
  onToggleColumn: PropTypes.func,
}

export default TableToolbar
